import React, { useState, useEffect, useMemo } from "react";
import "../JobPosting/jobs.scss";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { fetchJobById, updateJob } from "../../Redux/ActionCreators/jobsAction";
import {
  updateAssessmentSettings,
  getSelectedTests,
} from "../../Redux/ActionCreators/testLibraryAction";
import {
  setNavigationGuard,
  clearNavigationGuard,
} from "../../utils/navigationGuard";

// A test that can't run on a mobile device — when the assessment contains one,
// proctoring stays ON and the toggle is locked.
//
// The get-test response uses two shapes:
//   • Gappeo library tests  -> testType 1 (coding) / 6 (database) / 9 (data
//     science) / 11 (typing), testCategoryId 0.
//   • Custom / manual tests -> testType 7 or 8, with testCategoryId
//     1 (Programming) / 2 (Database) / 3 (Data science + Jupyter) / 4 (Typing)
//     and a matching testCategoryName.
// Note: `categoryName` (e.g. "Coding and App Development" on a CSS MCQ) is NOT a
// reliable signal — only `testCategoryName` / `testCategoryId` / `testType` are.
const getProctoringLockLabel = (tst) => {
  const type = Number(tst?.testType);
  const catId = Number(tst?.testCategoryId);
  const catName = String(tst?.testCategoryName || "").toLowerCase();

  if (type === 1 || catId === 1 || /program|coding/.test(catName)) return "Coding";
  if (type === 6 || catId === 2 || /database/.test(catName)) return "Database";
  if (type === 9 || catId === 3 || /data\s*science|jupyter/.test(catName))
    return "Data science";
  if (type === 11 || catId === 4 || /typing/.test(catName)) return "Typing";
  return null;
};

const CandidateReportSharing = ({ setType, setSnackbar }) => {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [saveLoading, setSaveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enableShare, setEnableShare] = useState(false);
  const [hideDetails, setHideDetails] = useState(false);
  const [enableReapply, setEnableReapply] = useState(false);
  const [maxApplications, setMaxApplications] = useState(1);
  const [cooldownDays, setCooldownDays] = useState(0);
  const [cvMode, setCvMode] = useState("mandatory");

  const [isProctoringEnabled, setIsProctoringEnabled] = useState(true);
  const [initialIsProctoringEnabled, setInitialIsProctoringEnabled] = useState(true);
  // null = still loading, "error" = fetch failed, array = the assessment's tests.
  const [assessmentTests, setAssessmentTests] = useState(null);
  // { open, nextValue }
  const [proctoringConfirm, setProctoringConfirm] = useState({
    open: false,
    nextValue: true,
  });
  // Unsaved-changes guard: { show, proceed } — proceed() runs the pending navigation.
  const [leaveConfirm, setLeaveConfirm] = useState({ show: false, proceed: null });

  const job = useSelector((state) => state.jobs.latestJob);

  const initialfetch = async () => {
    if (jobId) {
      setLoading(true);
      await dispatch(fetchJobById(jobId));
      setLoading(false);
    }
  };

  useEffect(() => {
    initialfetch();
  }, [jobId]);

  useEffect(() => {
    if (job) {
      setEnableShare(job?.enable_candidate_report_share ?? false);
      setHideDetails(job?.hide_candidate_details ?? false);

      const reapplySettings = job?.candidate_reapply_settings;
      setEnableReapply(reapplySettings?.enabled ?? false);
      setMaxApplications(reapplySettings?.max_applications ?? 1);
      setCooldownDays(reapplySettings?.cooldown_days ?? 0);

      setCvMode(job?.web_chat_settings?.cv_mode ?? "mandatory");
    }
  }, [job]);

  // Pull the linked assessment so we can (a) read the saved proctoring value and
  // (b) decide whether the toggle can be edited for this test mix. We read the
  // tests straight off the getSelectedTests response rather than the shared
  // redux slice so a stale value from another screen can't leak in.
  useEffect(() => {
    const testId = job?.assessment_id;
    if (!testId) return;
    let alive = true;
    (async () => {
      setAssessmentTests(null);
      const res = await dispatch(getSelectedTests(testId));
      if (!alive) return;
      const testObj = res?.payload;
      if (res?.type === "GET_TEST_DETAILS" && testObj) {
        const flag = testObj.isProctoringEnabled;
        const resolved = flag === undefined || flag === null ? true : !!flag;
        setIsProctoringEnabled(resolved);
        setInitialIsProctoringEnabled(resolved);
        setAssessmentTests(
          Array.isArray(testObj?.testDetails?.tests)
            ? testObj.testDetails.tests
            : []
        );
      } else {
        setAssessmentTests("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [job?.assessment_id]);

  const assessmentLoaded = Array.isArray(assessmentTests);

  const lockingTests = useMemo(() => {
    if (!Array.isArray(assessmentTests)) return [];
    const names = new Set();
    assessmentTests.forEach((tst) => {
      const label = getProctoringLockLabel(tst);
      if (label) names.add(label);
    });
    return Array.from(names);
  }, [assessmentTests]);

  const isProctoringEditable = assessmentLoaded && lockingTests.length === 0;

  // The proctoring toggle has an unsaved change.
  const isProctoringDirty = isProctoringEnabled !== initialIsProctoringEnabled;

  // Guard in-app navigation (Sidebar links, etc.) while proctoring is unsaved.
  useEffect(() => {
    if (isProctoringDirty) {
      setNavigationGuard((path) =>
        setLeaveConfirm({ show: true, proceed: () => navigate(path) })
      );
    } else {
      clearNavigationGuard();
    }
    return () => clearNavigationGuard();
  }, [isProctoringDirty, navigate]);

  // Guard tab close / refresh / typing a new URL while proctoring is unsaved.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isProctoringDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isProctoringDirty]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Run `proceed` now if nothing is unsaved, otherwise ask first.
  const attemptLeave = (proceed) => {
    if (isProctoringDirty) {
      setLeaveConfirm({ show: true, proceed });
    } else {
      proceed();
    }
  };

  const closeLeaveConfirm = () =>
    setLeaveConfirm({ show: false, proceed: null });

  const handleLeaveDiscard = () => {
    const proceed = leaveConfirm.proceed;
    setIsProctoringEnabled(initialIsProctoringEnabled);
    clearNavigationGuard();
    closeLeaveConfirm();
    proceed && proceed();
  };

  const handleLeaveSaveAndGo = async () => {
    const proceed = leaveConfirm.proceed;
    closeLeaveConfirm();
    const ok = await handleSave();
    // handleSave shows an error snackbar and keeps the user here on failure.
    if (!ok) return;
    clearNavigationGuard();
    proceed && proceed();
  };

  const requestProctoringChange = (nextValue) => {
    setProctoringConfirm({ open: true, nextValue });
  };

  const cancelProctoringChange = () => {
    setProctoringConfirm((prev) => ({ ...prev, open: false }));
  };

  const confirmProctoringChange = () => {
    setIsProctoringEnabled(proctoringConfirm.nextValue);
    setProctoringConfirm((prev) => ({ ...prev, open: false }));
  };

  const handleSave = async () => {
    if (!jobId) return false;

    setSaveLoading(true);

    const payload = {
      enable_candidate_report_share: enableShare,
      hide_candidate_details: hideDetails,
      candidate_reapply_settings: {
        enabled: enableReapply,
        max_applications: Number(maxApplications) || 1,
        cooldown_days: Number(cooldownDays) || 0,
      },
      web_chat_settings: {
        cv_mode: cvMode,
      },
    };

    const res = await dispatch(updateJob(jobId, payload));

    // Sync the linked assessment settings using the job's assessment_id as
    // testId. We call this when the reapply attempt limit needs syncing or when
    // the proctoring toggle changed.
    const testId = job?.assessment_id;
    const isProctoringChanged = isProctoringEnabled !== initialIsProctoringEnabled;
    if (testId && (enableReapply || isProctoringChanged)) {
      const settingsPayload = { testId };
      if (enableReapply) {
        settingsPayload.attemptLimit = Number(maxApplications) || 1;
      }
      if (isProctoringChanged || assessmentLoaded) {
        settingsPayload.isProctoringEnabled = isProctoringEnabled;
      }
      try {
        await dispatch(updateAssessmentSettings(settingsPayload));
        setInitialIsProctoringEnabled(isProctoringEnabled);
      } catch (err) {
        setSaveLoading(false);
        showSnackbar("Failed to save assessment settings", "error");
        return false;
      }
    }

    setSaveLoading(false);

    if (res?.success === false) {
      showSnackbar("Failed to save settings", "error");
      return false;
    }

    // SUCCESS
    showSnackbar("Settings saved successfully", "success");
    clearNavigationGuard();

    // Navigate immediately
    setType("");
    return true;
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => attemptLeave(() => setType(""))}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Candidate Settings</h3>
        </div>
      </div>

      <div className="job-settings-body">
        {loading ? (
          <div
            className="loading-overlay"
            style={{
              position: "relative",
              marginTop: 16,
              alignItems: "start",
              height: "32px",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="job-settings-form">
            <div className="vs-gappeo-card">
              <div className="vs-gappeo-card-info">
                <h5>Candidate reapply</h5>
              </div>
              <label className="toggle-switch toggle-pink">
                <input
                  type="checkbox"
                  checked={enableReapply}
                  onChange={(e) => setEnableReapply(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            {enableReapply && (
              <div className="job-settings-form-header">
                <div className="job-settings-form-score">
                  <label>Max applications</label>
                  <input
                    type="number"
                    min={1}
                    value={maxApplications}
                    onChange={(e) => setMaxApplications(e.target.value)}
                  />
                </div>
                <div className="job-settings-form-score">
                  <label>Cooldown days</label>
                  <input
                    type="number"
                    min={0}
                    value={cooldownDays}
                    onChange={(e) => setCooldownDays(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="job-settings-form-answer">
              <label>CV upload mode</label>
              <select
                value={cvMode}
                onChange={(e) => setCvMode(e.target.value)}
              >
                <option value="mandatory">Mandatory</option>
                <option value="optional">Optional</option>
                <option value="skip">Skip</option>
              </select>
            </div>

            {/* <div className="vs-gappeo-card">
              <div className="vs-gappeo-card-info">
                <h5>Proctoring</h5>
                <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>
                  When turned off, candidates can take this assessment on a
                  mobile device and webcam / full-screen / navigation violations
                  are not recorded.
                </p>
              </div>
              <label
                className="toggle-switch toggle-pink"
                style={{
                  opacity: isProctoringEditable ? 1 : 0.5,
                  cursor: isProctoringEditable ? "pointer" : "not-allowed",
                }}
              >
                <input
                  type="checkbox"
                  checked={isProctoringEnabled}
                  disabled={!isProctoringEditable}
                  onChange={(e) => requestProctoringChange(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div> */}

            {assessmentLoaded && lockingTests.length > 0 && (
              <p style={{ fontSize: 12, color: "#B45309", marginTop: -4 }}>
                Proctoring can&rsquo;t be changed because this assessment includes
                {lockingTests.length === 1 ? " a " : " "}
                {lockingTests.join(", ")}{" "}
                {lockingTests.length === 1 ? "test" : "tests"}, which must be
                taken on a desktop.
              </p>
            )}

            {assessmentTests === "error" && (
              <p style={{ fontSize: 12, color: "#B45309", marginTop: -4 }}>
                Couldn&rsquo;t load the linked assessment, so proctoring
                can&rsquo;t be changed right now. Please try again.
              </p>
            )}

            <div className="job-settings-form-header" style={{ flexDirection: "column" }}>
              <div className="job-settings-form-labels">
                <input
                  type="checkbox"
                  checked={enableShare}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setEnableShare(val);
                    if (!val) {
                      setHideDetails(false);
                    }
                  }}
                />
                <label>Enable candidate report share access</label>
              </div>
              <div className="job-settings-form-labels">
                <input
                  type="checkbox"
                  disabled={!enableShare}
                  checked={hideDetails}
                  onChange={(e) => setHideDetails(e.target.checked)}
                />
                <label>Hide candidate details in shared report</label>
              </div>
            </div>

            <button
              className="save-btn"
              disabled={saveLoading}
              onClick={handleSave}
            >
              {saveLoading ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>

      <Modal
        show={proctoringConfirm.open}
        centered
        onHide={cancelProctoringChange}
        dialogClassName="detail-modal"
      >
        <Modal.Body style={{ padding: "24px" }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
            {proctoringConfirm.nextValue
              ? "Turn proctoring on?"
              : "Turn proctoring off?"}
          </h3>
          <p style={{ fontSize: 14, color: "#374151", marginBottom: 8 }}>
            {proctoringConfirm.nextValue
              ? "Candidates will be required to take this assessment on a laptop or desktop, with webcam, full-screen and navigation checks enabled."
              : "Candidates will be able to take this assessment on a mobile device. Webcam violation, full-screen and tab-switch checks will no longer be recorded for this assessment."}
          </p>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#B45309", marginBottom: 20 }}>
            This changes how every candidate takes this assessment. The change
            applies to attempts started after you save.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              onClick={cancelProctoringChange}
              style={{
                border: "1px solid #D1D5DB",
                background: "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmProctoringChange}
              style={{
                border: "none",
                background: "#F75891",
                color: "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {proctoringConfirm.nextValue ? "Turn on" : "Turn off"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={leaveConfirm.show}
        centered
        onHide={closeLeaveConfirm}
        dialogClassName="detail-modal"
      >
        <Modal.Body style={{ padding: "24px" }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
            Unsaved changes
          </h3>
          <p style={{ fontSize: 14, color: "#374151", marginBottom: 20 }}>
            You&rsquo;ve changed the proctoring setting but haven&rsquo;t saved
            yet. Save your changes before leaving?
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={closeLeaveConfirm}
              style={{
                border: "1px solid #D1D5DB",
                background: "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Stay on page
            </button>
            <button
              onClick={handleLeaveDiscard}
              style={{
                border: "1px solid #D1D5DB",
                background: "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Discard &amp; leave
            </button>
            <button
              disabled={saveLoading}
              onClick={handleLeaveSaveAndGo}
              style={{
                border: "none",
                background: "#F75891",
                color: "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {saveLoading ? "Saving..." : "Save & leave"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CandidateReportSharing;
